import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageBreadcrumb from "../../../shared/PageBreadCrumb";
import CheckpointTable from "../components/CheckpointTable";
import CheckpointDetailsDrawer from "../components/CheckpointDetailsDrawer";

import { Checkpoint, AssetWithCheckpoints } from "../../../types/checkpoint";
import { Asset } from "../../../types/asset";
import projectTypeService, { ProjectType } from "../../../services/projectTypeService";
import { checkpointService } from "../../../services/checkpointService";
import { assetService } from "../../../services/assetService";





const ITEMS_PER_PAGE = 12;

export default function Checkpoints() {
  const navigate = useNavigate();
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalCheckpoints, setTotalCheckpoints] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [projectType, setProjectType] = useState<string>("");
  const [asset, setAsset] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("");
  const [status, setStatus] = useState<string | null>("Active");
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Drawer state
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);



  // Fetch project types and assets on component mount
  const fetchProjectTypesAndAssets = useCallback(async () => {
    try {
      const [projectTypesData, assetsData] = await Promise.all([
        projectTypeService.getAllProjectTypes(),
        assetService.getAssets({})
      ]);

      // Handle project types response - check for different possible structures
      let activeProjectTypes: ProjectType[] = [];
      if (projectTypesData && typeof projectTypesData === 'object') {
        if ('data' in projectTypesData && Array.isArray(projectTypesData.data)) {
          // Standard API response structure
          activeProjectTypes = projectTypesData.data.filter(pt => pt.status === 'active');
        } else if (Array.isArray(projectTypesData)) {
          // Direct array response
          activeProjectTypes = projectTypesData.filter(pt => pt.status === 'active');
        } else if ('docs' in projectTypesData && Array.isArray((projectTypesData as any).docs)) {
          // Paginated response structure
          activeProjectTypes = (projectTypesData as any).docs.filter((pt: any) => pt.status === 'active');
        }
      }

      setProjectTypes(activeProjectTypes);
      console.log('Project types loaded:', activeProjectTypes);

      // Set first project type as default only if there's exactly one project type
      if (activeProjectTypes.length === 1 && !projectType) {
        setProjectType(activeProjectTypes[0]._id);
      }

      // Handle asset response structure - assetService.getAssets returns { assets: Asset[], totalDocs: number, pagination?: any }
      let assetsArray: Asset[] = [];
      if (assetsData && typeof assetsData === 'object') {
        if ('assets' in assetsData && Array.isArray(assetsData.assets)) {
          // Correct structure from assetService.getAssets
          assetsArray = assetsData.assets;
        } else if ('data' in assetsData && Array.isArray(assetsData.data)) {
          // Standard API response structure
          assetsArray = assetsData.data;
        } else if (Array.isArray(assetsData)) {
          // Direct array response
          assetsArray = assetsData;
        } else if ('docs' in assetsData && Array.isArray((assetsData as any).docs)) {
          // Paginated response structure
          assetsArray = (assetsData as any).docs;
        } else if ('meta' in assetsData && 'data' in assetsData && Array.isArray((assetsData as any).data)) {
          // Another possible paginated structure
          assetsArray = (assetsData as any).data;
        }
      }

      setAssets(assetsArray);
      console.log('Assets loaded:', assetsArray);

    } catch (error) {
      console.error("Failed to fetch project types and assets:", error);
    }
  }, [projectType]);

  const fetchCheckpoints = useCallback(async () => {
    try {
      const params: any = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: `${sortField}:${sortOrder}`,
      };

      if (search) params.search = search;
      if (projectType) params.projectTypeId = projectType;
      if (asset) params.assetId = asset;
      if (frequency) params.frequency = frequency;
      if (status && (status === 'active' || status === 'Inactive')) {
        params.status = status;
      }

      const response = await checkpointService.getCheckpoints(params);

      // Handle different possible response structures
      let checkpointsArray: Checkpoint[] = [];
      let totalDocs = 0;

      if (response && typeof response === 'object') {
        if ('data' in response && Array.isArray(response.data)) {
          // Direct array in data
          checkpointsArray = response.data;
        } else if ('data' in response && response.data && typeof response.data === 'object') {
          if ('checkpoints' in response.data && Array.isArray(response.data.checkpoints)) {
            // Nested checkpoints array
            checkpointsArray = response.data.checkpoints;
          } else if (Array.isArray(response.data)) {
            // Data is an array
            checkpointsArray = response.data;
          }
        }

        // Handle pagination info
        if ('pagination' in response && response.pagination) {
          totalDocs = (response.pagination as any).totalDocs || 0;
        } else if ('meta' in response && response.meta) {
          totalDocs = (response.meta as any).totalDocs || 0;
        } else if ('data' in response && response.data && typeof response.data === 'object' && 'pagination' in response.data) {
          totalDocs = (response.data as any).pagination?.totalDocs || 0;
        }
      }

      setCheckpoints(checkpointsArray);
      setTotalCheckpoints(totalDocs);
    } catch (err) {
      console.error("Failed to fetch checkpoints", err);
      setCheckpoints([]);
      setTotalCheckpoints(0);
    }
  }, [search, projectType, asset, frequency, status, page, sortField, sortOrder]);

  useEffect(() => {
    fetchCheckpoints();
    fetchProjectTypesAndAssets();
  }, [fetchCheckpoints, fetchProjectTypesAndAssets]);

  const handleView = (checkpoint: Checkpoint) => {
    setSelectedCheckpointId(checkpoint._id);
    setIsDrawerOpen(true);
  };





  const handleAdd = (assetData?: AssetWithCheckpoints) => {
    if (assetData) {
      // If called from AssetCheckpointsDrawer with assetData, just refresh the data
      // This means a checkpoint was created in the drawer, so we need to refresh
      fetchCheckpoints();
      return;
    }

    // Navigate to new checkpoint form only when called from main page (without assetData)
    const searchParams = new URLSearchParams();

    // Use current filter values if no asset data provided
    if (projectType) searchParams.set('projectTypeId', projectType);
    if (asset) searchParams.set('assetId', asset);

    const queryString = searchParams.toString();
    navigate(`/masters/checkpoints/new${queryString ? `?${queryString}` : ''}`);
  };

  const handleBulkAdd = () => {
    // Navigate to bulk checkpoint form with optional pre-filled data
    const searchParams = new URLSearchParams();
    if (projectType) searchParams.set('projectTypeId', projectType);
    if (asset) searchParams.set('assetId', asset);

    const queryString = searchParams.toString();
    navigate(`/masters/checkpoints/bulk${queryString ? `?${queryString}` : ''}`);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <PageBreadcrumb
        items={[
          { name: "Home", path: "/masters" },
          { name: "Checkpoints", path: "/masters/checkpoints" },
        ]}
      />

      <CheckpointTable
        checkpoints={checkpoints}
        search={search}
        setSearch={setSearch}
        projectType={projectType}
        setProjectType={setProjectType}
        asset={asset}
        setAsset={setAsset}
        frequency={frequency}
        setFrequency={setFrequency}
        status={status}
        setStatus={setStatus}
        page={page}
        setPage={setPage}
        totalCheckpoints={totalCheckpoints}
        projectTypes={projectTypes}
        assets={assets}

        onView={handleView}
        onAdd={handleAdd}
        onBulkAdd={handleBulkAdd}
        onRefresh={fetchCheckpoints}
        sortField={sortField}
        sortOrder={sortOrder}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
      />

      {/* Checkpoint Details Drawer */}
      <CheckpointDetailsDrawer
        checkpointId={selectedCheckpointId}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedCheckpointId(null);
        }}

      />
    </div>
  );
} 